import { type ZodSchema, type ZodTypeDef, z } from 'zod'
import { customProjectIconSchema } from '@/project'
import {
  namespaceSchema,
  nanoidSchema,
  slugSchema,
  timestampSchema,
} from '@/utility'
import { featureFlagSchema } from '@/team/feature-flags'

export enum TeamUserRole {
  /** The owner of the team.
   *
   * Permissions:
   * - Add new admins
   * - Switch to a new owner
   * - Billing management
   * - Delete Team
   */
  Owner = 'owner',
  /**
   * Admins
   *
   * Permissions:
   * - Add new members
   * - Remove members
   * - Delete projects and documents
   */
  Admin = 'admin',
  /**
   * Members
   *
   * Permissions:
   * - View the team and its projects
   * - Create new documents
   */
  Editor = 'editor',
  /**
   * Viewers have readonly access to the team and its projects
   *
   * Eventually we may allow extending permissions on a fine-grained level
   */
  Viewer = 'viewer',
}

export const roleLabels: Record<TeamUserRole, string> = {
  [TeamUserRole.Admin]: 'Admin',
  [TeamUserRole.Editor]: 'Editor',
  [TeamUserRole.Owner]: 'Owner',
  [TeamUserRole.Viewer]: 'Viewer',
} as const

// ---------------------------------------------------------------------------

export const roleSchema: ZodSchema<TeamUserRole, ZodTypeDef, any> =
  z.nativeEnum(TeamUserRole)

export type UserRef = {
  uid: string
  role: TeamUserRole
  displayName: string
  imageUri?: string
  activeProjectUid: string
}

export const userRefSchema = z.object({
  uid: nanoidSchema,
  role: roleSchema,
  displayName: z.string(),
  imageUri: z.string().optional(),
  activeProjectUid: z.string().default(''),
}) satisfies ZodSchema<UserRef, ZodTypeDef, any>

// ---------------------------------------------------------------------------

export enum BillingPlan {
  /** Trial plan */
  Trial = 'trial',
  /** Free plan */
  Free = 'free',
  /** Pro (Monthly) plan */
  ProMonthly = 'pro_monthly',
  /** Pro (Yearly) plan */
  ProYearly = 'pro_yearly',
  /** Premium plan @deprecated The premium plan has been deprecated */
  Premium = 'premium',
  /** Enterprise plan */
  Enterprise = 'enterprise',
}

export const billingPlanLabels: {
  [key in BillingPlan]: string
} = {
  [BillingPlan.Free]: 'Free',
  [BillingPlan.Trial]: 'Trial',
  [BillingPlan.ProMonthly]: 'Monthly',
  [BillingPlan.Premium]: 'Monthly',
  [BillingPlan.ProYearly]: 'Yearly',
  [BillingPlan.Enterprise]: 'Enterprise',
}

export const billingPlanSchema = z.nativeEnum(BillingPlan)

export type BillingInfo = {
  billingId?: string
  /** The plan the team is on */
  plan: z.infer<typeof billingPlanSchema>
  /** Unix timestamp of expiry date in seconds */
  expires?: number
}

export const billingInfoSchema = z.preprocess(
  (arg: any) => {
    if (
      typeof arg?.hasTrial === 'boolean' &&
      ![
        BillingPlan.Premium,
        BillingPlan.ProMonthly,
        BillingPlan.ProYearly,
      ].includes(arg?.plan)
    ) {
      arg.plan = 'trial'
    }
    return arg
  },
  z.object({
    billingId: nanoidSchema.optional(),
    plan: billingPlanSchema,
    expires: z.number().optional(),
  }),
) satisfies ZodSchema<BillingInfo, ZodTypeDef, any>

export const invoiceStatusSchema = z.enum([
  'open',
  'paid',
  'draft',
  'void',
  'uncollectible',
])

export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>

export const invoiceStatusLabels: {
  [key in InvoiceStatus]: string
} = {
  'open': 'Open',
  'paid': 'Paid',
  'void': 'Void',
  'draft': 'Draft',
  'uncollectible': 'Uncollectible',
}

export const billingInvoiceSchema = z.object({
  id: z.string(),
  /** Total invoice payment amount */
  total: z.number(),
  /** Invoice status - we should only display open and paid to users  */
  status: invoiceStatusSchema.nullable(),
  /** Invoice payment date */
  paymentDate: z.number(),
  /** Currency for payment */
  currency: z.string(),
  /** PDF download link */
  downloadUrl: z.string().nullable().optional(),
})

export type BillingInvoice = z.infer<typeof billingInvoiceSchema>

export const cardSchema = z.object({
  last4: z.string().length(4),
  expMonth: z.number(),
  expYear: z.number(),
})

export type BillingCard = z.infer<typeof cardSchema>

export const teamBillingSummary = z.object({
  /** All invoices for a team */
  invoices: billingInvoiceSchema.array().default([]),
  /** Stripe customer billing address */
  address: z.string().nullable(),
  card: cardSchema.nullable(),
  /** Basic Scalar billing info from team record */
  billingInfo: billingInfoSchema,
})

export type TeamBillingSummary = z.infer<typeof teamBillingSummary>

// ---------------------------------------------------------------------------

export const pendingInviteSchema = z.object({
  uid: nanoidSchema,
  role: roleSchema.default(TeamUserRole.Editor),
  email: z.string().email(),
})

export type PendingInvite = z.infer<typeof pendingInviteSchema>

// ---------------------------------------------------------------------------

export const teamNameSchema = z
  .string()
  .trim()
  .transform((s) => (s.length ? s : 'Untitled Team'))

export const teamImageSchema = z.string().optional()

/**
 * Onboarding state
 *
 * This is in an enum in case
 * we decide to add extra steps
 * to it.
 */
export enum TeamOnboardingState {
  Setup = 'setup',
  Complete = 'complete',
}

export const teamSchema = z.object({
  uid: nanoidSchema,
  name: teamNameSchema,
  slug: slugSchema,
  imageUri: teamImageSchema,
  email: z
    .string()
    .email()
    .transform((s) => s.toLowerCase()),
  theme: z.string().default('default'),
  onboarding: z
    .nativeEnum(TeamOnboardingState)
    .default(TeamOnboardingState.Complete),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  billingInfo: billingInfoSchema,
  namespaces: namespaceSchema.array().default([]),
  projects: nanoidSchema.array(),
  users: userRefSchema.array(),
  icons: customProjectIconSchema.array().default([]),
  pendingInvites: pendingInviteSchema.array().default([]),
  githubRepos: z
    .record(z.union([z.number(), z.string()]), z.boolean())
    .default({}),
  features: featureFlagSchema.default({}),
})

export type Team = z.infer<typeof teamSchema>

// ---------------------------------------------------------------------------

/** Records used to determine if teams are restricted to federated auth only */
export type TeamControls = {
  teamUid: string
  /** Domains to allow users from ex. ['apidocumentation.com','scalar.com'] */
  domains: string[]
  preventPasswordAuth: boolean
}

export const teamControlsSchema = z.object({
  uid: nanoidSchema,
  teamUid: nanoidSchema,
  domains: z.string().min(5).array(),
  preventPasswordAuth: z.boolean(),
})
